import React, { useEffect, useMemo } from 'react'
import { matchRoutes, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AppRouteConfig, appRoutes } from './routeConfig'
import { Helmet } from 'react-helmet-async'
import { AuthRoute } from './AuthRoute'
import { ProtectedRoute } from './ProtectedRoute'
import { logEvent } from '~/utils/firebase'

function wrapRouteElement (route: AppRouteConfig) {
  const content = (
    <>
      {route.title && (
        <Helmet>
          <title>{route.title}</title>
        </Helmet>
      )}

      {route.element}
    </>
  )

  switch (route.guard) {
  case 'auth':
    return <AuthRoute>{content}</AuthRoute>
  case 'protected':
    return <ProtectedRoute>{content}</ProtectedRoute>
  default:
    return content
  }
}

const routeMatchConfig = appRoutes.map((route) => ({
  path: route.path
}))

const routeConfigByPath = new Map(
  appRoutes.map((route) => [route.path, route])
)

function Navigator () {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const matches = matchRoutes(routeMatchConfig, location)
    const matchedPath = matches?.[matches.length - 1]?.route.path
    const routeConfig = matchedPath ? routeConfigByPath.get(matchedPath) : undefined

    if (!routeConfig) return

    logEvent('screen_view', {
      screen_name: routeConfig.name,
      screen_class: routeConfig.name,
      page_path: location.pathname
    })
  }, [location])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const action = urlParams.get('action')
    const resetPasswordToken = urlParams.get('resetPasswordToken')

    if (action === 'reset-password' && resetPasswordToken) {
      navigate(
        `/reset-password?resetPasswordToken=${resetPasswordToken}&action=reset-password`,
        { replace: true }
      )

      return
    }

    if (action === 'register' && urlParams.get('token') && urlParams.get('email')) {
      navigate(
        `/registration?action=register&token=${urlParams.get('token')}&email=${urlParams.get('email')}`,
        { replace: true }
      )
    }
  }, [navigate])

  const memoisedAppRoutes = useMemo(
    () =>
      appRoutes.map((route) => (
        <Route
          key={route.name}
          path={route.path}
          element={wrapRouteElement(route)}
        />
      )),
    []
  )

  return <Routes>{memoisedAppRoutes}</Routes>
}

export default Navigator
